import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GoogleLogo } from "@phosphor-icons/react";

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      login(res.data.token, res.data.user);
      toast.success("Bentornato!");
      const r = res.data.user.role;
      navigate(r === "admin" ? "/admin" : r === "professionista" ? "/pro" : "/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore accesso");
    } finally { setLoading(false); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid place-items-center px-4 py-10">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-card border border-border rounded-2xl p-7 shadow-sm">
        <img src="/nexa-mark.png" alt="NEXA" className="w-12 h-12 rounded-xl object-contain mb-4" />
        <h1 className="font-heading text-2xl font-bold">Accedi a NEXA</h1>
        <p className="text-muted-foreground text-sm mt-1">Prenota i migliori professionisti vicino a te.</p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" className="mt-1.5" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <Link to="/forgot-password" className="text-xs text-primary font-medium" data-testid="forgot-link">Password dimenticata?</Link>
            </div>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" className="mt-1.5" />
          </div>
          <Button type="submit" className="w-full rounded-full" disabled={loading} data-testid="login-submit">
            {loading ? "Accesso…" : "Accedi"}
          </Button>
        </form>

        <div className="flex items-center gap-3 my-5"><div className="h-px flex-1 bg-border" /><span className="text-xs text-muted-foreground">oppure</span><div className="h-px flex-1 bg-border" /></div>
        <Button variant="outline" className="w-full rounded-full gap-2" onClick={google} data-testid="login-google">
          <GoogleLogo weight="bold" /> Continua con Google
        </Button>

        <p className="text-sm text-center text-muted-foreground mt-6">
          Non hai un account? <Link to="/register" className="text-primary font-medium" data-testid="to-register">Registrati</Link>
        </p>
        <div className="mt-4 text-xs text-muted-foreground bg-secondary rounded-lg p-3">
          Demo: cliente@nexa.com / cliente123 · pro0@nexa.com / pro123 · admin@nexa.com / admin123
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
